import { Definition, WebModuleDefinition, WebSurfaceDefinition } from '../';
import { ProcessDefinition } from './props';

/**
 * Extract props (fields) from the web module definition.
 * @property - Type definition of a Web module created using createModule function.
 * @example
 *
 * ```ts
 * import {ExtractPropsFromModuleDefinition} from '@atlassiansox/bxp-definition-sdk'
 * import {ExampleWebModule} from './../web-modules'


 * type ExampleWebModuleProps = ExtractPropsFromModuleDefinition<typeof ExampleWebModule>

 *  export function ExampleReactComponent({content}: ExampleWebModuleProps) {

 *    const {
 *      isProduction,
 *      title
 *    } = content;
 *  }
 *  .
 *  ...
 *  ....
 *
 *  <ExampleReactComponent content = {{ isProduction : true, title : "WAC Signup Bundle"}}></ExampleReactComponent>
 * ```
 **/
export type ExtractPropsFromDefinition<T extends Definition> =
  ProcessDefinition<T>;

/**
 * Extract props (fields) from the web module definition of the specified version.
 * @property Type definition of a Web module created using createModule function.
 * @example
 *
 * ```ts
 * import {ExtractPropsFromModuleDefinitionVersion} from '@atlassiansox/bxp-definition-sdk'
 * import {ExampleWebModule} from './../web-modules'


 * type ExampleWebModuleV0Props = ExtractPropsFromModuleDefinitionVersion<
 *    typeof ExampleWebModule,
 *    1
 * >

 *  export function ExampleReactComponent({content}: ExampleWebModuleV1Props) {

 *    const {
 *      isProduction,
 *      title
 *    } = content;
 *  }
 *  .
 *  ...
 *  ....
 *
 *  <ExampleReactComponent content = {{ isProduction : true, title : "WAC Signup Bundle"}}></ExampleReactComponent>
 * ```
 **/

export type ExtractPropsFromDefinitionVersion<
  T extends WebModuleDefinition | WebSurfaceDefinition,
  U extends number
> = ExtractPropsFromDefinition<T>['versions'][U] extends { fields: infer F }
  ? ExtractPropsFromDefinition<T>['versions'][U] extends { zones: infer Z }
    ? F & { zones: Z }
    : F
  : never;
